import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Beaker, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ALL = [
  // Majors / minors
  "XAU_USD", "USD_JPY", "EUR_USD", "GBP_USD", "AUD_USD",
  "USD_CAD", "NZD_USD", "USD_CHF", "EUR_JPY", "GBP_JPY", "XAG_USD",
  // Crypto
  "BTC_USD", "ETH_USD",
  // Indices
  "US30", "US100", "US500",
];

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [firingTopstep, setFiringTopstep] = useState(false);
  const [tsFireSymbol, setTsFireSymbol] = useState("MGC");
  const [tsFireSide, setTsFireSide] = useState("BUY");
  const [testPair, setTestPair] = useState("");
  const [testSide, setTestSide] = useState("BUY");

  useEffect(() => {
    api.get("/bot/settings").then((r) => setS(r.data));
    api.get("/strategies").then((r) => setStrategies(r.data.strategies || []));
  }, []);

  // Keep `testPair` in sync with the user's monitored pairs.
  // If the current testPair isn't in the list, snap to the first monitored pair.
  useEffect(() => {
    if (!s?.pairs?.length) return;
    if (!s.pairs.includes(testPair)) {
      setTestPair(s.pairs[0]);
    }
  }, [s?.pairs, testPair]);

  if (!s) return <div className="p-8 text-zinc-500">Loading…</div>;

  const locked = !!s.locked_by_admin;

  const togglePair = (p) => {
    const pairs = s.pairs.includes(p) ? s.pairs.filter((x) => x !== p) : [...s.pairs, p];
    setS({ ...s, pairs });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Sanitize: empty-string or NaN numerics → safe defaults so backend validation
      // doesn't reject. Specifically `be_at_tp_pct` allows draft "" during typing.
      const payload = { ...s };
      // Preserve whatever execution_broker the user has selected (mt5 or topstep).
      // Legacy users with 'oanda' or 'both' still auto-migrate to 'mt5' below.
      if (!["mt5", "topstep"].includes(payload.execution_broker)) {
        payload.execution_broker = "mt5";
      }
      if (payload.be_at_tp_pct === "" || !Number.isFinite(payload.be_at_tp_pct)) {
        payload.be_at_tp_pct = 50;
      }
      await api.put("/bot/settings", payload);
      toast.success("Strategy saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const forceTest = async () => {
    if (!["shooter_mama_1m", "quick_scalper_v2", "obos_stoch_1m", "bon_voyage_m200a", "ny_killzone_reversal", "cash_money", "volume_spike_scalper"].includes(s.strategy_id)) {
      toast.error("Switch to a force-able strategy and save first");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post(`/strategy/force-test?pair=${testPair}&side=${testSide}`);
      const r = data.result?.results?.[0];
      if (r) {
        toast.success(`Fired ${r.executions}× ${r.side} ${r.pair} @ ${r.entry?.toFixed(3)} (lot=${r.lot}, marti=${r.marti_level})`);
      } else {
        toast.info(`No execution: ${data.result?.skipped || "unknown"}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const fireTopstep = async () => {
    setFiringTopstep(true);
    try {
      const { data } = await api.post("/brokers/topstep/fire-test", {
        symbol: tsFireSymbol,
        side: tsFireSide,
      });
      if (data.ok && data.trade_ids?.length) {
        toast.success(`TopStep manual fire OK — ${data.trade_ids.length} trade(s) placed on ${tsFireSymbol} ${tsFireSide}`);
      } else if (data.errors?.length) {
        toast.error(`TopStep fire failed: ${data.errors[0]}`);
      } else {
        toast.info("TopStep: no trade placed (check symbol allow-list + credentials)");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "TopStep manual fire failed");
    } finally { setFiringTopstep(false); }
  };

  const set = (k, v) => setS({ ...s, [k]: v });
  const activeStrat = strategies.find((x) => x.id === s.strategy_id);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Configuration</div>
        <h1 className="text-xl sm:text-2xl font-medium tracking-tight">Strategy</h1>
        <p className="text-sm text-zinc-500 mt-1">Pick a strategy and tune its parameters.</p>
      </div>

      {/* Admin lock banner */}
      {locked && (
        <div data-testid="settings-locked-banner"
             className="border border-amber-500/40 bg-amber-500/[0.06] rounded-sm p-4 flex items-start gap-3">
          <Lock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div className="text-xs text-amber-200/90 leading-relaxed">
            <div className="font-mono uppercase tracking-widest text-amber-400 text-[10px] mb-1">
              Settings locked by administrator
            </div>
            Your strategy, pairs, and risk controls are being managed for you. To request a change,
            contact your administrator.
          </div>
        </div>
      )}

      <fieldset disabled={locked} className={locked ? "opacity-60 pointer-events-none select-none" : ""}>

      {/* Strategy selector */}
      <div className="border border-white/10 bg-[#121214] rounded-sm p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Active Strategy</div>
        <select value={s.strategy_id} onChange={(e) => set("strategy_id", e.target.value)}
                data-testid="strategy-select"
                className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-zinc-500 focus:outline-none">
          {strategies.map((st) => (
            <option key={st.id} value={st.id}>{st.name} · {st.timeframe}</option>
          ))}
        </select>
        <div className="text-xs text-zinc-500 leading-relaxed font-mono pt-1">
          Choose the strategy that fits your trading style. Internal mechanics are proprietary.
        </div>
      </div>

      <div className="border border-white/10 bg-[#121214] rounded-sm p-5 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Pairs Monitored</div>
          <div className="flex flex-wrap gap-2">
            {ALL.map((p) => {
              const on = s.pairs.includes(p);
              return (
                <button key={p} data-testid={`pair-toggle-${p}`} onClick={() => togglePair(p)}
                        className={`px-2 py-1 text-xs font-mono uppercase tracking-widest border rounded-sm ${
                          on ? "bg-teal-400/10 text-teal-400 border-teal-400/30"
                             : "border-white/10 text-zinc-500 hover:border-white/20"
                        }`}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Lot Size (manual priority)">
            <input type="number" step="0.01" min="0.01" value={s.lot_size}
                   onChange={(e) => set("lot_size", parseFloat(e.target.value) || 0.01)}
                   data-testid="lot-size-input"
                   className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
          </Field>
          <Field label="Trades / Signal">
            <input type="number" step="1" min="1" max="50" value={s.trades_per_signal}
                   onChange={(e) => set("trades_per_signal", parseInt(e.target.value || "1"))}
                   data-testid="trades-per-signal-input"
                   className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
          </Field>
          {s.strategy_id === "rejection_wick_5m" && <>
            <Field label="Stop Loss (pips)">
              <input type="number" step="0.1" min="0" value={s.stop_loss_pips}
                     onChange={(e) => set("stop_loss_pips", parseFloat(e.target.value) || 0)}
                     data-testid="stop-loss-input"
                     className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
            </Field>
            <Field label="Take Profit (pips)">
              <input type="number" step="0.1" min="0" value={s.take_profit_pips}
                     onChange={(e) => set("take_profit_pips", parseFloat(e.target.value) || 0)}
                     data-testid="take-profit-input"
                     className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
            </Field>
          </>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Mode">
            <select value={s.mode} onChange={(e) => set("mode", e.target.value)}
                    data-testid="mode-select"
                    className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none">
              <option value="demo">demo (practice)</option>
              <option value="live">live (real)</option>
            </select>
          </Field>
        </div>

        {/* Strategy-specific params for Quick Scalper V2 */}
        {s.strategy_id === "quick_scalper_v2" && (
          <div className="border border-cyan-400/20 bg-cyan-400/5 rounded-sm p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-cyan-400">Strategy Parameters</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="EMA Fast Period">
                <input type="number" step="1" min="2" max="200"
                       value={s.strategy_params?.ema_fast ?? 9}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, ema_fast: parseInt(e.target.value) || 9})}
                       data-testid="qsv2-ema-fast-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="EMA Slow Period">
                <input type="number" step="1" min="2" max="400"
                       value={s.strategy_params?.ema_slow ?? 21}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, ema_slow: parseInt(e.target.value) || 21})}
                       data-testid="qsv2-ema-slow-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Touch Tolerance (pips)">
                <input type="number" step="0.5" min="0"
                       value={s.strategy_params?.touch_tolerance_pips ?? 1}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, touch_tolerance_pips: parseFloat(e.target.value) || 0})}
                       data-testid="qsv2-tol-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Take Profit (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.tp_pips ?? 10}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, tp_pips: parseFloat(e.target.value) || 10})}
                       data-testid="qsv2-tp-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Stop Loss (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.sl_pips ?? 10}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, sl_pips: parseFloat(e.target.value) || 10})}
                       data-testid="qsv2-sl-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Cooldown (sec)">
                <input type="number" step="1" min="0"
                       value={s.strategy_params?.cooldown_sec ?? 30}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, cooldown_sec: parseInt(e.target.value) || 0})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Break-even Trigger (×TP)">
                <input type="number" step="0.05" min="0.1" max="1"
                       value={s.strategy_params?.be_percent ?? 0.85}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, be_percent: parseFloat(e.target.value) || 0.85})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Force Close (sec, if profit > 0)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.force_profit_sec ?? 60}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, force_profit_sec: parseInt(e.target.value) || 60})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
            </div>
            <div className="text-[10px] text-cyan-400/70 leading-relaxed">
              Adjust execution sizing, risk, and timing for this strategy. Defaults are tuned for typical scalping conditions.
            </div>
          </div>
        )}

        {/* Strategy-specific params for NY Killzone Reversal */}
        {s.strategy_id === "ny_killzone_reversal" && (
          <div className="border border-rose-400/20 bg-rose-400/5 rounded-sm p-4 space-y-3" data-testid="ny-killzone-params-panel">
            <div className="text-[10px] uppercase tracking-widest text-rose-400">NY Killzone — Parameters</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Entry Time (ET, HH:MM)">
                <input type="text" placeholder="09:30"
                       value={s.strategy_params?.entry_time_et ?? "09:30"}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, entry_time_et: e.target.value})}
                       data-testid="ny-entry-time-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Take Profit (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.tp_pips ?? 40}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, tp_pips: parseFloat(e.target.value) || 40})}
                       data-testid="ny-tp-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Stop Loss (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.sl_pips ?? 20}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, sl_pips: parseFloat(e.target.value) || 20})}
                       data-testid="ny-sl-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Doji Threshold (pips)">
                <input type="number" step="0.1" min="0"
                       value={s.strategy_params?.doji_threshold_pips ?? 0.5}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, doji_threshold_pips: parseFloat(e.target.value) || 0.5})}
                       data-testid="ny-doji-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Eval Window (sec)">
                <input type="number" step="30" min="60"
                       value={s.strategy_params?.eval_window_sec ?? 300}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, eval_window_sec: parseInt(e.target.value) || 300})}
                       data-testid="ny-eval-window-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
            </div>
            <div className="text-[10px] text-rose-400/70 leading-relaxed">
              At the configured NY time (default 09:30 ET), wait for that 5-min candle to close.
              Bullish candle → SELL · Bearish candle → BUY (reversal). Fires ONCE per pair per day.
              Doji threshold filters out tiny indecision candles.
            </div>
          </div>
        )}

        {/* Strategy-specific params for Bon voyage M200A */}
        {s.strategy_id === "bon_voyage_m200a" && (
          <div className="border border-sky-400/20 bg-sky-400/5 rounded-sm p-4 space-y-3" data-testid="bon-voyage-params-panel">
            <div className="text-[10px] uppercase tracking-widest text-sky-400">Strategy Parameters</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="MA Length">
                <input type="number" step="1" min="10" max="500"
                       value={s.strategy_params?.ma_length ?? 200}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, ma_length: parseInt(e.target.value) || 200})}
                       data-testid="bv-ma-length-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Touch Threshold (pips)">
                <input type="number" step="0.5" min="0.1"
                       value={s.strategy_params?.touch_threshold_pips ?? 2}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, touch_threshold_pips: parseFloat(e.target.value) || 2})}
                       data-testid="bv-touch-threshold-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Trend Min Distance (pips)">
                <input type="number" step="0.5" min="0.1"
                       value={s.strategy_params?.trend_min_distance_pips ?? 5}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, trend_min_distance_pips: parseFloat(e.target.value) || 5})}
                       data-testid="bv-trend-distance-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Take Profit (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.tp_pips ?? 30}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, tp_pips: parseFloat(e.target.value) || 30})}
                       data-testid="bv-tp-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Stop Loss (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.sl_pips ?? 20}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, sl_pips: parseFloat(e.target.value) || 20})}
                       data-testid="bv-sl-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Cooldown (sec) — between trades">
                <input type="number" step="1" min="0"
                       value={s.strategy_params?.cooldown_sec ?? 1800}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, cooldown_sec: parseInt(e.target.value) || 0})}
                       data-testid="bv-cooldown-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
            </div>
            <div className="text-[10px] text-sky-400/70 leading-relaxed">
              MA200 dynamic support/resistance bounce. BUY when price wicks down to MA200 from above + closes back above. Mirror for SELL.
              Touch threshold = how close the wick must get to MA200. Trend distance = how far prev candle must be from MA200 to consider the trend valid.
            </div>
          </div>
        )}

        {/* Strategy-specific params for Cash Money (TylerTicks PineScript port) */}
        {s.strategy_id === "cash_money" && (
          <div className="border border-emerald-400/20 bg-emerald-400/5 rounded-sm p-4 space-y-3" data-testid="cash-money-params-panel">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Strategy Parameters</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Trend Sensitivity (len)">
                <input type="number" step="1" min="1" max="100"
                       value={s.strategy_params?.len ?? 10}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, len: parseInt(e.target.value) || 10})}
                       data-testid="cm-len-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="ATR Multiplier">
                <input type="number" step="0.1" min="0.1" max="10"
                       value={s.strategy_params?.mult ?? 1.5}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, mult: parseFloat(e.target.value) || 1.5})}
                       data-testid="cm-mult-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="ATR Length">
                <input type="number" step="1" min="1" max="100"
                       value={s.strategy_params?.atr_length ?? 10}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, atr_length: parseInt(e.target.value) || 10})}
                       data-testid="cm-atr-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Min Bars Between Signals">
                <input type="number" step="1" min="1" max="200"
                       value={s.strategy_params?.min_bars_between ?? 8}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, min_bars_between: parseInt(e.target.value) || 8})}
                       data-testid="cm-cooldown-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Wick Rejection Ratio (0-1)">
                <input type="number" step="0.05" min="0" max="1"
                       value={s.strategy_params?.wick_ratio ?? 0.6}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, wick_ratio: parseFloat(e.target.value) || 0.6})}
                       data-testid="cm-wick-ratio-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Take Profit (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.tp_pips ?? 30}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, tp_pips: parseFloat(e.target.value) || 30})}
                       data-testid="cm-tp-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Stop Loss (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.sl_pips ?? 20}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, sl_pips: parseFloat(e.target.value) || 20})}
                       data-testid="cm-sl-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
            </div>
            <div className="text-[10px] text-emerald-400/70 leading-relaxed">
              Trend-Confirmed Candle (TylerTicks). BUY on -1→+1 flip + green candle + strong lower wick; SELL on +1→-1 flip + red candle + strong upper wick.
              Signals alternate and respect the N-bar cooldown. Works on any forex/metals pair (e.g., XAUUSD, EURUSD).
            </div>

            {/* Optional time-based exit — mirrors the Volume Spike scalper */}
            <div className="pt-3 mt-2 border-t border-emerald-400/10" data-testid="cm-time-exit-section">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 pr-4">
                  <div className="text-[10px] uppercase tracking-widest text-orange-400">Optional Time-Based Exit</div>
                  <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                    Force-close every Cash Money trade after N seconds if it's a winner or a loser. Broker-side SL / TP still act as a safety net. Great for scalping XAUUSD around news candles.
                  </div>
                </div>
                <button
                  data-testid="cm-time-exit-toggle"
                  type="button"
                  onClick={() => set("strategy_params", {...s.strategy_params, time_exit_enabled: !(s.strategy_params?.time_exit_enabled ?? false)})}
                  className={`relative shrink-0 w-10 h-5 rounded-full transition-colors focus:outline-none ${(s.strategy_params?.time_exit_enabled ?? false) ? "bg-orange-400" : "bg-zinc-700"}`}
                  aria-pressed={!!(s.strategy_params?.time_exit_enabled ?? false)}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${(s.strategy_params?.time_exit_enabled ?? false) ? "translate-x-5" : ""}`} />
                </button>
              </div>
              {(s.strategy_params?.time_exit_enabled ?? false) && (
                <div className="grid grid-cols-2 gap-3 mt-3" data-testid="cm-time-exit-inputs">
                  <Field label="Profit Exit (seconds)">
                    <input type="number" step="1" min="1" max="60"
                           value={s.strategy_params?.profit_close_seconds ?? 3}
                           onChange={(e) => set("strategy_params", {...s.strategy_params, profit_close_seconds: parseInt(e.target.value) || 3})}
                           data-testid="cm-profit-sec-input"
                           className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-teal-400/50 focus:outline-none" />
                  </Field>
                  <Field label="Loss Exit (seconds)">
                    <input type="number" step="1" min="1" max="60"
                           value={s.strategy_params?.loss_close_seconds ?? 5}
                           onChange={(e) => set("strategy_params", {...s.strategy_params, loss_close_seconds: parseInt(e.target.value) || 5})}
                           data-testid="cm-loss-sec-input"
                           className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-rose-400/50 focus:outline-none" />
                  </Field>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Strategy-specific params for Volume Spike Scalper */}
        {s.strategy_id === "volume_spike_scalper" && (
          <div className="border border-orange-400/20 bg-orange-400/5 rounded-sm p-4 space-y-3" data-testid="vss-params-panel">
            <div className="text-[10px] uppercase tracking-widest text-orange-400">Strategy Parameters</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Volume Lookback (bars)">
                <input type="number" step="1" min="5" max="200"
                       value={s.strategy_params?.volume_lookback ?? 20}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, volume_lookback: parseInt(e.target.value) || 20})}
                       data-testid="vss-lookback-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Spike Multiplier (×avg)">
                <input type="number" step="0.1" min="1.1" max="20"
                       value={s.strategy_params?.spike_multiplier ?? 2.0}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, spike_multiplier: parseFloat(e.target.value) || 2.0})}
                       data-testid="vss-spike-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Min Body Ratio (0-1)">
                <input type="number" step="0.05" min="0.1" max="1"
                       value={s.strategy_params?.min_body_ratio ?? 0.6}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, min_body_ratio: parseFloat(e.target.value) || 0.6})}
                       data-testid="vss-body-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Close Position Min (0-1)">
                <input type="number" step="0.05" min="0.5" max="1"
                       value={s.strategy_params?.close_position_min ?? 0.65}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, close_position_min: parseFloat(e.target.value) || 0.65})}
                       data-testid="vss-closepos-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Cooldown (sec) — between fires">
                <input type="number" step="5" min="0"
                       value={s.strategy_params?.cooldown_sec ?? 30}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, cooldown_sec: parseInt(e.target.value) || 0})}
                       data-testid="vss-cooldown-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Profit Exit (seconds)">
                <input type="number" step="1" min="1" max="60"
                       value={s.strategy_params?.profit_close_seconds ?? 3}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, profit_close_seconds: parseInt(e.target.value) || 3})}
                       data-testid="vss-profit-sec-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-teal-400/50 focus:outline-none" />
              </Field>
              <Field label="Loss Exit (seconds)">
                <input type="number" step="1" min="1" max="60"
                       value={s.strategy_params?.loss_close_seconds ?? 5}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, loss_close_seconds: parseInt(e.target.value) || 5})}
                       data-testid="vss-loss-sec-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-rose-400/50 focus:outline-none" />
              </Field>
            </div>
            <div className="text-[10px] text-orange-400/70 leading-relaxed">
              No broker-side SL/TP — this strategy exits by TIME. In profit → close after {s.strategy_params?.profit_close_seconds ?? 3}s. In loss → close after {s.strategy_params?.loss_close_seconds ?? 5}s. Fires only on M1 candles whose volume exceeds {s.strategy_params?.spike_multiplier ?? 2.0}× the {s.strategy_params?.volume_lookback ?? 20}-bar average with a body-dominant close near the extreme.
            </div>
          </div>
        )}

        {/* Strategy-specific params for OBOS Stochastic */}
        {s.strategy_id === "obos_stoch_1m" && (
          <div className="border border-fuchsia-400/20 bg-fuchsia-400/5 rounded-sm p-4 space-y-3" data-testid="obos-params-panel">
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-400">Strategy Parameters</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="%K Period">
                <input type="number" step="1" min="2" max="50"
                       value={s.strategy_params?.k_period ?? 5}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, k_period: parseInt(e.target.value) || 5})}
                       data-testid="obos-k-period-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="%K Smoothing">
                <input type="number" step="1" min="1" max="20"
                       value={s.strategy_params?.smooth_k ?? 3}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, smooth_k: parseInt(e.target.value) || 3})}
                       data-testid="obos-smooth-k-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="%D Period">
                <input type="number" step="1" min="1" max="20"
                       value={s.strategy_params?.d_period ?? 3}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, d_period: parseInt(e.target.value) || 3})}
                       data-testid="obos-d-period-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Oversold Level (BUY ≤)">
                <input type="number" step="1" min="1" max="50"
                       value={s.strategy_params?.oversold_level ?? 10}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, oversold_level: parseFloat(e.target.value) || 10})}
                       data-testid="obos-oversold-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Overbought Level (SELL ≥)">
                <input type="number" step="1" min="50" max="100"
                       value={s.strategy_params?.overbought_level ?? 90}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, overbought_level: parseFloat(e.target.value) || 90})}
                       data-testid="obos-overbought-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Cooldown (sec) — after win OR loss">
                <input type="number" step="1" min="0"
                       value={s.strategy_params?.cooldown_sec ?? 60}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, cooldown_sec: parseInt(e.target.value) || 0})}
                       data-testid="obos-cooldown-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-fuchsia-400/50 focus:outline-none" />
              </Field>
              <Field label="Take Profit (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.tp_pips ?? 10}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, tp_pips: parseFloat(e.target.value) || 10})}
                       data-testid="obos-tp-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Stop Loss (pips)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.sl_pips ?? 10}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, sl_pips: parseFloat(e.target.value) || 10})}
                       data-testid="obos-sl-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Break-even Trigger (×TP)">
                <input type="number" step="0.05" min="0.1" max="1"
                       value={s.strategy_params?.be_percent ?? 0.85}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, be_percent: parseFloat(e.target.value) || 0.85})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
            </div>
            <div className="text-[10px] text-fuchsia-400/70 leading-relaxed">
              Reversal logic with configurable thresholds and cooldown timer. The cooldown is enforced after EVERY closed trade — no entries fire until it elapses.
            </div>
          </div>
        )}

        {/* Strategy-specific params for Shooter MAMA */}
        {s.strategy_id === "shooter_mama_1m" && (
          <div className="border border-amber-400/20 bg-amber-400/5 rounded-sm p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-400">Strategy Parameters</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Wick Ratio (×body)">
                <input type="number" step="0.05" min="0.1" max="3"
                       value={s.strategy_params?.wick_ratio ?? 0.65}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, wick_ratio: parseFloat(e.target.value) || 0.65})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Break-even Trigger (×TP)">
                <input type="number" step="0.05" min="0.1" max="1"
                       value={s.strategy_params?.be_percent ?? 0.85}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, be_percent: parseFloat(e.target.value) || 0.85})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Cooldown (sec)">
                <input type="number" step="1" min="0"
                       value={s.strategy_params?.cooldown_sec ?? 60}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, cooldown_sec: parseInt(e.target.value) || 0})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Force Close (sec, if profit > 0)">
                <input type="number" step="1" min="1"
                       value={s.strategy_params?.force_profit_sec ?? 50}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, force_profit_sec: parseInt(e.target.value) || 50})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Force Profit (pips)">
                <input type="number" step="10" min="0"
                       value={s.strategy_params?.force_profit_pips ?? 1800}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, force_profit_pips: parseInt(e.target.value) || 1800})}
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Take Profit Target (pips) — 0 = use wick 1R">
                <input type="number" step="1" min="0"
                       value={s.strategy_params?.tp_pips ?? 0}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, tp_pips: parseFloat(e.target.value) || 0})}
                       data-testid="sm-tp-pips-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Risk / Stop Loss (pips) — 0 = behind wick">
                <input type="number" step="1" min="0"
                       value={s.strategy_params?.sl_pips ?? 0}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, sl_pips: parseFloat(e.target.value) || 0})}
                       data-testid="sm-sl-pips-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
              <Field label="Min Prev Range (pips)">
                <input type="number" step="1" min="0"
                       value={s.strategy_params?.min_range_pips ?? 15}
                       onChange={(e) => set("strategy_params", {...s.strategy_params, min_range_pips: parseFloat(e.target.value) || 0})}
                       data-testid="sm-min-range-input"
                       className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none" />
              </Field>
            </div>
            <div className="text-[10px] text-amber-400/70 leading-relaxed">
              Martingale automatically scales lot size on losses: 1× → 2× → 3× → wraps to 1× (one win resets to 1×). Lot above is the BASE lot.
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-white/10" data-testid="reverse-on-signal-row">
          <div className="min-w-0 pr-4">
            <div className="text-[10px] uppercase tracking-widest text-teal-400">Reverse on Opposite Signal</div>
            <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
              When ON: if a fresh SELL signal arrives while a BUY is still open on the same pair, the BUY is closed at market first (even if in profit) and the SELL fires immediately. Guarantees one direction per pair, per user.
            </div>
          </div>
          <button
            data-testid="reverse-on-signal-toggle"
            type="button"
            onClick={() => set("reverse_on_signal", !(s.reverse_on_signal ?? true))}
            disabled={locked}
            className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none disabled:opacity-50 ${((s.reverse_on_signal ?? true)) ? "bg-teal-400" : "bg-zinc-700"}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${((s.reverse_on_signal ?? true)) ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <div className="text-xs text-zinc-500">
            Bot enabled: <span className={s.bot_enabled ? "text-teal-400" : "text-zinc-500"}>{s.bot_enabled ? "yes" : "no"}</span>
          </div>
          <button data-testid="save-settings-button" onClick={save} disabled={saving}
                  className="bg-zinc-100 text-zinc-900 hover:bg-white px-4 py-2 rounded-sm text-sm font-medium disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Account-level Risk Controls (hard auto-stop) */}
      <div className="border border-rose-400/20 bg-[#121214] rounded-sm p-5 space-y-4" data-testid="risk-controls-panel">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-rose-400">Account Risk Controls</div>
          <div className="text-sm text-zinc-400 leading-relaxed mt-1.5">
            Auto-stop the bot when account equity crosses either threshold (measured against your
            starting balance). Set to <span className="font-mono text-zinc-300">0</span> to disable a limit.
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Max Drawdown (%)">
            <input type="number" step="0.5" min="0" max="100"
                   value={s.risk_max_drawdown_pct ?? 0}
                   onChange={(e) => set("risk_max_drawdown_pct", parseFloat(e.target.value) || 0)}
                   data-testid="risk-drawdown-input"
                   className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-rose-400/50 focus:outline-none" />
            <div className="text-[10px] text-zinc-500 mt-1">e.g. <span className="text-rose-400">3</span> = stop if equity drops 3% below start</div>
          </Field>
          <Field label="Take Profit Target (%)">
            <input type="number" step="0.5" min="0" max="10000"
                   value={s.risk_take_profit_pct ?? 0}
                   onChange={(e) => set("risk_take_profit_pct", parseFloat(e.target.value) || 0)}
                   data-testid="risk-takeprofit-input"
                   className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-teal-400/50 focus:outline-none" />
            <div className="text-[10px] text-zinc-500 mt-1">e.g. <span className="text-teal-400">10</span> = stop after equity is 10% above start</div>
          </Field>
          <Field label="Max Drawdown ($ risk)">
            <input type="number" step="10" min="0"
                   value={s.risk_max_drawdown_dollars ?? 0}
                   onChange={(e) => set("risk_max_drawdown_dollars", parseFloat(e.target.value) || 0)}
                   data-testid="risk-drawdown-dollars-input"
                   className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-rose-400/50 focus:outline-none" />
            <div className="text-[10px] text-zinc-500 mt-1">e.g. <span className="text-rose-400">300</span> = stop if session loss reaches $300</div>
          </Field>
          <Field label="Take Profit Target ($)">
            <input type="number" step="10" min="0"
                   value={s.risk_take_profit_dollars ?? 0}
                   onChange={(e) => set("risk_take_profit_dollars", parseFloat(e.target.value) || 0)}
                   data-testid="risk-takeprofit-dollars-input"
                   className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-teal-400/50 focus:outline-none" />
            <div className="text-[10px] text-zinc-500 mt-1">e.g. <span className="text-teal-400">500</span> = stop after session profit reaches $500</div>
          </Field>
        </div>
        <div className="text-[10px] text-zinc-600 font-mono pt-2 border-t border-white/5">
          // Whichever threshold hits first ($ or %) triggers the auto-stop. Bot enters a cool-down until the next 5:00 PM EST session close.
        </div>

        {/* Auto-reset starting balance toggle */}
        <div className="pt-3 mt-2 border-t border-white/5" data-testid="auto-reset-balance-section">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 pr-4">
              <div className="text-[10px] uppercase tracking-widest text-amber-400">Auto-Reset Starting Balance on Stop</div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                When the bot stops (manually or by hitting a target), rebase the starting balance to your current equity so the next session begins fresh at 0 PnL.
              </div>
            </div>
            <button
              data-testid="auto-reset-balance-toggle"
              type="button"
              onClick={() => set("auto_reset_starting_balance", !(s.auto_reset_starting_balance ?? true))}
              className={`relative shrink-0 w-10 h-5 rounded-full transition-colors focus:outline-none ${((s.auto_reset_starting_balance ?? true)) ? "bg-amber-400" : "bg-zinc-700"}`}
              aria-pressed={!!(s.auto_reset_starting_balance ?? true)}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${((s.auto_reset_starting_balance ?? true)) ? "translate-x-5" : ""}`} />
            </button>
          </div>
        </div>

        {/* Break-even at 1R toggle */}
        <div className="pt-4 mt-2 border-t border-white/5 space-y-3" data-testid="be-at-1r-section">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-teal-400">Move SL to Break-Even at 1R</div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                When floating profit reaches the size of your stop loss (1:1 risk-reward), the SL automatically jumps to entry + a small profit buffer to cover spread. Trade becomes risk-free.
              </div>
            </div>
            <button
              data-testid="be-at-1r-toggle"
              type="button"
              onClick={() => set("be_at_1r_enabled", !s.be_at_1r_enabled)}
              className={`relative shrink-0 w-10 h-5 rounded-full transition-colors focus:outline-none ${s.be_at_1r_enabled ? "bg-teal-400" : "bg-zinc-700"}`}
              aria-pressed={!!s.be_at_1r_enabled}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${s.be_at_1r_enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>
        </div>

        {/* Break-even at % of TP toggle */}
        <div className="pt-4 mt-2 border-t border-white/5 space-y-3" data-testid="be-at-tp-section">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-teal-400">Move SL to Break-Even at % of TP</div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                When price has travelled the configured percentage from entry toward
                your take-profit target, the SL automatically jumps to entry + a small
                profit buffer. Default <span className="text-teal-400">50%</span> — your
                trade becomes risk-free halfway to TP.
              </div>
            </div>
            <button
              data-testid="be-at-tp-toggle"
              type="button"
              onClick={() => set("be_at_tp_enabled", !s.be_at_tp_enabled)}
              className={`relative shrink-0 w-10 h-5 rounded-full transition-colors focus:outline-none ${s.be_at_tp_enabled ? "bg-teal-400" : "bg-zinc-700"}`}
              aria-pressed={!!s.be_at_tp_enabled}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${s.be_at_tp_enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>
          {s.be_at_tp_enabled && (
            <Field label="% of TP distance to trigger BE">
              <input type="number" step="5" min="10" max="95"
                     value={s.be_at_tp_pct ?? ""}
                     onChange={(e) => {
                       const raw = e.target.value;
                       if (raw === "") return set("be_at_tp_pct", "");
                       const n = parseFloat(raw);
                       if (!Number.isNaN(n)) set("be_at_tp_pct", n);
                     }}
                     onBlur={(e) => {
                       // Snap-clamp on blur only — backend requires 10 ≤ x ≤ 95
                       const n = parseFloat(e.target.value);
                       if (Number.isNaN(n)) return set("be_at_tp_pct", 50);
                       if (n < 10) return set("be_at_tp_pct", 10);
                       if (n > 95) return set("be_at_tp_pct", 95);
                     }}
                     data-testid="be-at-tp-pct-input"
                     className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-teal-400/50 focus:outline-none" />
              <div className="text-[10px] text-zinc-500 mt-1">
                Range: 10–95. Lower = protects profit earlier · Higher = lets trade run longer before locking in.
              </div>
            </Field>
          )}
        </div>

        {/* Break-even at fixed $ profit — works for MT5 (uses live floating profit)
            and TopStep (computes $ from price × contracts × point_value). */}
        <div className="pt-4 mt-2 border-t border-white/5 space-y-3" data-testid="be-at-dollar-section">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-teal-400">Move SL to Break-Even at $ Profit</div>
              <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
                When floating profit reaches the dollar amount below, SL auto-snaps to entry —
                locking the trade risk-free. Works on both MT5 (uses live account-currency profit)
                and TopStep futures (computes from price × contracts × $/point).
              </div>
            </div>
            <button
              data-testid="be-at-dollar-toggle"
              type="button"
              onClick={() => set("be_at_dollar_enabled", !s.be_at_dollar_enabled)}
              className={`relative shrink-0 w-10 h-5 rounded-full transition-colors focus:outline-none ${s.be_at_dollar_enabled ? "bg-teal-400" : "bg-zinc-700"}`}
              aria-pressed={!!s.be_at_dollar_enabled}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${s.be_at_dollar_enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>
          {s.be_at_dollar_enabled && (
            <Field label="Floating profit trigger ($)">
              <input type="number" step="5" min="0"
                     value={s.be_at_dollar_amount ?? 100}
                     onChange={(e) => set("be_at_dollar_amount", parseFloat(e.target.value) || 0)}
                     data-testid="be-at-dollar-input"
                     className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-teal-400/50 focus:outline-none" />
              <div className="text-[10px] text-zinc-500 mt-1">
                Example: <span className="text-teal-400">$100</span> — the moment the trade shows +$100 floating profit, SL moves to entry. Fires once per trade.
              </div>
            </Field>
          )}
        </div>

        {/* Shared spread buffer for BOTH BE features */}
        {(s.be_at_1r_enabled || s.be_at_tp_enabled) && (
          <div className="pt-2">
            <Field label="Spread buffer (pips above/below entry)">
              <input type="number" step="0.1" min="0" max="50"
                     value={s.be_buffer_pips ?? 1}
                     onChange={(e) => set("be_buffer_pips", parseFloat(e.target.value) || 0)}
                     data-testid="be-buffer-input"
                     className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-teal-400/50 focus:outline-none" />
              <div className="text-[10px] text-zinc-500 mt-1">
                Default: <span className="text-teal-400">1.0</span> — secures a tiny profit that absorbs the typical spread. Applies to both BE features above.
              </div>
            </Field>
          </div>
        )}
      </div>

      {/* Force-test panel placeholder ↓ */}
      <div className="hidden">spacer</div>

      {/* TopStep / ProjectX Futures panel — visible only when admin has granted access */}
      {user?.topstep_enabled && (
      <div className="border border-amber-400/25 bg-[#121214] rounded-sm p-5 space-y-4" data-testid="topstep-futures-panel">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-2">
            <span>⚡</span>
            <span>TopStep Futures</span>
          </div>
          <div className="text-sm text-zinc-400 leading-relaxed mt-1.5">
            Route your existing strategy signals to TopStep prop-firm futures accounts (GC / MGC / NQ / MNQ) via the ProjectX Gateway API.
            Connect your account on the <span className="text-zinc-300">Brokers</span> page first, then configure the trade sizing below.
            SL / TP are entered in <span className="text-zinc-300">dollars per contract</span>.
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Execution route</div>
          <select
            data-testid="execution-broker-select"
            value={s.execution_broker || "mt5"}
            onChange={(e) => set("execution_broker", e.target.value)}
            disabled={locked}
            className="bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none disabled:opacity-50"
          >
            <option value="mt5">MT5 (forex / metals / indices)</option>
            <option value="topstep">TopStep Futures (GC / MGC / NQ / MNQ)</option>
          </select>
          <div className="text-[10px] text-zinc-600 mt-1">
            When set to TopStep, all strategies fire ONLY on the selected futures below. Forex signals are ignored.
          </div>
        </div>

        {/* Auto-trade toggle — independent of forex bot switch */}
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/5">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-400">Auto-Trade Futures</div>
            <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
              Runs the selected strategy on the futures allow-list below using ProjectX candles.
              Turn OFF to pause futures without touching your MT5 forex bot.
            </div>
          </div>
          <button
            data-testid="topstep-bot-toggle"
            type="button"
            onClick={() => set("topstep_bot_enabled", !s.topstep_bot_enabled)}
            className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none ${s.topstep_bot_enabled ? "bg-amber-400" : "bg-zinc-700"}`}
            aria-pressed={!!s.topstep_bot_enabled}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${s.topstep_bot_enabled ? "translate-x-5" : ""}`} />
          </button>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Allowed futures</div>
          <div className="flex flex-wrap gap-1.5" data-testid="topstep-pairs-selector">
            {[
              { sym: "MGC", label: "MGC · Micro Gold · $10/pt" },
              { sym: "GC",  label: "GC · Gold · $100/pt" },
              { sym: "MNQ", label: "MNQ · Micro Nasdaq · $2/pt" },
              { sym: "NQ",  label: "NQ · Nasdaq · $20/pt" },
            ].map(({ sym, label }) => {
              const active = (s.topstep_pairs || []).includes(sym);
              return (
                <button
                  key={sym}
                  type="button"
                  data-testid={`topstep-pair-${sym}`}
                  disabled={locked}
                  onClick={() => {
                    const cur = s.topstep_pairs || [];
                    const next = cur.includes(sym) ? cur.filter(x => x !== sym) : [...cur, sym];
                    set("topstep_pairs", next);
                  }}
                  className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors disabled:opacity-50 ${
                    active
                      ? "bg-amber-400/20 border-amber-400/60 text-amber-300"
                      : "bg-black border-white/10 text-zinc-400 hover:border-white/25"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-zinc-600 mt-1.5">
            Tap to toggle. Empty = no futures allowed (bot won&apos;t fire on TopStep).
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Contracts per trade">
            <input
              type="number"
              min="1"
              step="1"
              data-testid="topstep-contracts-input"
              value={s.topstep_contracts ?? 1}
              disabled={locked}
              onChange={(e) => set("topstep_contracts", parseInt(e.target.value || "1", 10))}
              className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none disabled:opacity-50"
            />
          </Field>
          <Field label="Stop Loss ($ per contract)">
            <input
              type="number"
              min="0"
              step="1"
              data-testid="topstep-sl-dollars-input"
              value={s.topstep_sl_dollars ?? 50}
              disabled={locked}
              onChange={(e) => set("topstep_sl_dollars", parseFloat(e.target.value || "0"))}
              className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none disabled:opacity-50"
            />
          </Field>
          <Field label="Take Profit ($ per contract)">
            <input
              type="number"
              min="0"
              step="1"
              data-testid="topstep-tp-dollars-input"
              value={s.topstep_tp_dollars ?? 100}
              disabled={locked}
              onChange={(e) => set("topstep_tp_dollars", parseFloat(e.target.value || "0"))}
              className="w-full bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none disabled:opacity-50"
            />
          </Field>
        </div>

        <div className="text-[10px] text-zinc-500 leading-relaxed border-l-2 border-amber-400/40 pl-3 py-1.5">
          <span className="text-amber-300">Per-contract dollar risk</span> — with 3 contracts and $50 SL, total account risk is $150.
          The bot converts your dollar amounts to price ticks using each contract&apos;s spec (MGC $10/point, GC $100/point, MNQ $2/point, NQ $20/point).
        </div>

        {/* Manual "Fire TopStep test trade" — bypasses signal detection */}
        <div className="pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Manual TopStep fire</div>
          <div className="text-xs text-zinc-400 leading-relaxed mb-3">
            Skip the strategy signal and fire an immediate market order using your current contracts + $SL + $TP.
            Useful to sanity-check the broker pipeline. <span className="text-amber-300">Save settings first.</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <Field label="Symbol">
              <select
                value={tsFireSymbol}
                onChange={(e) => setTsFireSymbol(e.target.value)}
                data-testid="topstep-fire-symbol"
                disabled={locked}
                className="bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none disabled:opacity-50"
              >
                <option value="MGC">MGC · Micro Gold</option>
                <option value="GC">GC · Gold</option>
                <option value="MNQ">MNQ · Micro Nasdaq</option>
                <option value="NQ">NQ · Nasdaq</option>
              </select>
            </Field>
            <Field label="Side">
              <select
                value={tsFireSide}
                onChange={(e) => setTsFireSide(e.target.value)}
                data-testid="topstep-fire-side"
                disabled={locked}
                className="bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none disabled:opacity-50"
              >
                <option>BUY</option>
                <option>SELL</option>
              </select>
            </Field>
            <button
              onClick={fireTopstep}
              disabled={firingTopstep || locked}
              data-testid="topstep-fire-button"
              className="bg-amber-400/10 text-amber-300 border border-amber-400/40 hover:bg-amber-400/20 px-4 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest disabled:opacity-50"
            >
              {firingTopstep ? "Firing…" : "Fire TopStep trade"}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Force-test panel */}
      {(s.strategy_id === "shooter_mama_1m" || s.strategy_id === "quick_scalper_v2" || s.strategy_id === "obos_stoch_1m" || s.strategy_id === "bon_voyage_m200a" || s.strategy_id === "ny_killzone_reversal" || s.strategy_id === "cash_money" || s.strategy_id === "volume_spike_scalper") && (
        <div className="border border-rose-400/20 bg-rose-400/5 rounded-sm p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Beaker className="w-4 h-4 text-rose-400" />
            <div className="text-[10px] uppercase tracking-widest text-rose-400">Force Test Trade</div>
          </div>
          <div className="text-xs text-zinc-400 leading-relaxed">
            Bypass the entry signal and immediately fire a multi-trade execution at current market price using your current lot size, trades/signal, and martingale level. Use this to verify the broker pipeline. <span className="text-rose-300">Save settings first.</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <Field label="Pair">
              <select value={testPair} onChange={(e) => setTestPair(e.target.value)}
                      data-testid="test-pair-select"
                      className="bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none">
                {(s.pairs || ALL).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Side">
              <select value={testSide} onChange={(e) => setTestSide(e.target.value)}
                      data-testid="test-side-select"
                      className="bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none">
                <option>BUY</option>
                <option>SELL</option>
              </select>
            </Field>
            <button onClick={forceTest} disabled={testing}
                    data-testid="force-test-button"
                    className="bg-rose-400/10 text-rose-400 border border-rose-400/30 hover:bg-rose-400/20 px-4 py-1.5 rounded-sm text-xs font-mono uppercase tracking-widest disabled:opacity-50">
              {testing ? "Firing…" : "Fire test trade"}
            </button>
          </div>
        </div>
      )}
      </fieldset>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
